// Global
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IProductFilter } from '@/models/products.model';

// Components
import { StyledSelect } from '../Styled';
import { StyledHelperText } from './SliderFilterSelectStyles';

// Helpers
import { getFilterDefaultValue } from '../../helpers/productCharacteristicsHelpers';

interface SliderFilterSelectProps {
  filter: IProductFilter;
  options: { value: string; label: string }[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
}

const SliderFilterSelect = ({ filter, options, value, onChange }: SliderFilterSelectProps) => {
  const { t } = useTranslation('products');
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;

    const filtered = options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()));
    const exactMatch = options.find((option) => option.label.toLowerCase() === searchValue.toLowerCase());

    if (!exactMatch && searchValue.trim()) {
      return [
        ...filtered,
        {
          value: `custom_${searchValue}_${Date.now()}`,
          label: searchValue,
          isCustom: true,
          customValue: searchValue,
        },
      ];
    }

    return filtered;
  }, [options, searchValue]);

  const handleClear = () => {
    setSearchValue('');

    if (onChange) {
      onChange('');
    }
  };

  const handleSelectionChange = (selectedValues: string | string[]) => {
    const valuesArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
    const newValues: string[] = [];

    valuesArray.forEach((valuesItem) => {
      if (typeof valuesItem === 'string' && valuesItem.startsWith('custom_')) {
        const customValue = valuesItem.split('_').slice(1, -1).join('_');
        if (customValue) {
          newValues.push(customValue);
        }
      } else if (valuesItem) {
        newValues.push(valuesItem);
      }
    });

    if (onChange) {
      onChange(newValues);
    }
  };

  return (
    <>
      <StyledSelect
        allowClear
        showSearch
        placeholder={t('characteristics_filters_placeholder')}
        options={filteredOptions}
        onSearch={setSearchValue}
        onClear={handleClear}
        defaultValue={getFilterDefaultValue(filter)}
        onChange={(values) => {
          handleSelectionChange(values as string | string[]);
        }}
        value={value}
        maxTagCount={3}
      />
      <StyledHelperText>
        {t('characteristics_min_max_helper_text', {
          min: Number(filter.min),
          max: Number(filter.max),
        })}
      </StyledHelperText>
    </>
  );
};

export default SliderFilterSelect;
